import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { User, Session } from '@supabase/supabase-js'
import { UserProfile } from '../types'
import { logUserAction } from '../services/userLogs'
import { checkUserExists, normalizeEmail, normalizePersonName, resolveSignUpResult } from '../utils/userValidation'

interface AuthState {
  user: User | null
  profile: UserProfile | null
  session: Session | null
  loading: boolean
  error: string | null
}

interface AuthResult {
  success: boolean
  error?: string
}

const loginSessionKey = (userId: string) => `login_logged_${userId}`

const LOGIN_LOG_MAX_RETRIES = 3
const LOGIN_LOG_RETRY_BASE_MS = 1000

/** Evita tentativas concorrentes duplicadas (ex.: React Strict Mode) */
const loginLogInFlight = new Map<string, Promise<void>>()

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    profile: null,
    session: null,
    loading: true,
    error: null
  })

  // Carregar perfil do usuário
  const loadProfile = useCallback(async (userId: string) => {
    try {
      const selectWithDepartment = `
          *,
          user_group:user_groups(*),
          department:departments(id, name, display_name)
        `
      const selectBase = `
          *,
          user_group:user_groups(*)
        `

      const withDepartment = await supabase
        .from('profiles')
        .select(selectWithDepartment)
        .eq('id', userId)
        .single()

      let data = withDepartment.data
      if (withDepartment.error) {
        const fallback = await supabase
          .from('profiles')
          .select(selectBase)
          .eq('id', userId)
          .single()
        if (fallback.error) throw fallback.error
        data = fallback.data
      }

      if (data && Array.isArray((data as { department?: unknown }).department)) {
        const department = (data as { department: unknown[] }).department
        data = { ...data, department: department[0] ?? null }
      }

      return data
    } catch (error) {
      console.error('Erro ao carregar perfil:', error)
      return null
    }
  }, [])

  // Verificar se o usuário está ativo
  const checkUserActive = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('is_active')
        .eq('id', userId)
        .single()

      if (error) throw error

      return data?.is_active || false
    } catch (error) {
      console.error('Erro ao verificar status do usuário:', error)
      return false
    }
  }, [])

  // Atualizar último login
  const updateLastLogin = useCallback(async (userId: string) => {
    try {
      await supabase
        .from('profiles')
        .update({ last_login: new Date().toISOString() })
        .eq('id', userId)
    } catch (error) {
      console.error('Erro ao atualizar último login:', error)
    }
  }, [])

  const clearLoginSessionFlag = useCallback((userId: string) => {
    try {
      sessionStorage.removeItem(loginSessionKey(userId))
    } catch {
      // Erro silencioso ao limpar flag de sessão
    }
  }, [])

  // Registra log de login uma vez por sessão do navegador (sessionStorage é limpo ao fechar)
  const recordLoginSession = useCallback(async (userId: string) => {
    if (typeof window === 'undefined') return

    const storageKey = loginSessionKey(userId)
    if (sessionStorage.getItem(storageKey) === '1') return

    const inFlight = loginLogInFlight.get(userId)
    if (inFlight) {
      await inFlight
      return
    }

    const task = (async () => {
      for (let attempt = 1; attempt <= LOGIN_LOG_MAX_RETRIES; attempt++) {
        const logged = await logUserAction({
          action: 'login',
          entityType: 'other',
          entityId: null,
          description: 'Login realizado no sistema',
          userId,
        })

        if (logged) {
          await updateLastLogin(userId)
          sessionStorage.setItem(storageKey, '1')
          return
        }

        if (attempt < LOGIN_LOG_MAX_RETRIES) {
          await sleep(LOGIN_LOG_RETRY_BASE_MS * attempt)
        }
      }

      console.warn(
        `Não foi possível registrar log de login após ${LOGIN_LOG_MAX_RETRIES} tentativas. ` +
        'Uma nova tentativa ocorrerá ao recarregar a página.'
      )
    })()

    loginLogInFlight.set(userId, task)

    try {
      await task
    } finally {
      loginLogInFlight.delete(userId)
    }
  }, [updateLastLogin])

  // Inicializar autenticação
  useEffect(() => {
    let mounted = true

    const initializeAuth = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()

        if (!mounted) return

        if (sessionError) {
          setAuthState({
            user: null,
            profile: null,
            session: null,
            loading: false,
            error: sessionError.message
          })
          return
        }

        if (session?.user) {
          const profile = await loadProfile(session.user.id)

          if (!mounted) return

          setAuthState({
            user: session.user,
            profile,
            session,
            loading: false,
            error: null
          })

          // Não bloquear acesso enquanto registra o log (executar fora do fluxo de auth)
          void recordLoginSession(session.user.id)
        } else {
          setAuthState({
            user: null,
            profile: null,
            session: null,
            loading: false,
            error: null
          })
        }
      } catch (error) {
        if (!mounted) return

        setAuthState({
          user: null,
          profile: null,
          session: null,
          loading: false,
          error: (error as Error).message
        })
      }
    }

    initializeAuth()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event) => {
        // Apenas SIGNED_OUT aqui — callbacks async com queries Supabase em SIGNED_IN
        // causam deadlock no cliente de auth e impedem login/sessão de completar.
        if (event === 'SIGNED_OUT') {
          setAuthState({
            user: null,
            profile: null,
            session: null,
            loading: false,
            error: null
          })
        }
      }
    )

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [loadProfile, recordLoginSession])

  // Login
  const signIn = async (email: string, password: string): Promise<AuthResult> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }))

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) throw error

              if (data.user) {
          // Verificar se o usuário está ativo
          const isActive = await checkUserActive(data.user.id)
          
          if (!isActive) {
            setAuthState(prev => ({
              ...prev,
              loading: false,
              error: 'Usuário inativo. Entre em contato com o administrador.'
            }))
            return { success: false, error: 'Usuário inativo. Entre em contato com o administrador.' }
          }

          const profile = await loadProfile(data.user.id)

        setAuthState({
          user: data.user,
          profile,
          session: data.session,
          loading: false,
          error: null
        })

        void recordLoginSession(data.user.id)

        return { success: true }
      }

      return { success: false, error: 'Erro no login' }
    } catch (error) {
      const errorMessage = (error as Error).message
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  // Registro
  const signUp = async (email: string, password: string, name?: string): Promise<AuthResult> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }))

      const normalizedEmail = normalizeEmail(email)
      const userCheck = await checkUserExists(normalizedEmail)
      if (userCheck.exists) {
        const duplicateMessage =
          'Este e-mail já está cadastrado. Use outro e-mail ou recupere sua senha.'
        setAuthState(prev => ({ ...prev, loading: false, error: duplicateMessage }))
        return { success: false, error: duplicateMessage }
      }

      const normalizedName = name ? normalizePersonName(name) : undefined
      const { data, error } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
          data: {
            name: normalizedName
          }
        }
      })

      const result = await resolveSignUpResult(data, error, normalizedEmail)

      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: result.success ? null : result.message
      }))

      return {
        success: result.success,
        error: result.message
      }
    } catch (error) {
      const errorMessage = (error as Error).message
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  // Logout
  const signOut = async (): Promise<AuthResult> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }))

      // Capturar user_id antes do logout para registrar o log
      const userIdBeforeLogout = authState.user?.id

      // Limpar cache de permissões e flag de login antes do logout
      if (authState.user) {
        try {
          sessionStorage.removeItem(`permissions_${authState.user.id}`)
          clearLoginSessionFlag(authState.user.id)
        } catch {
          // Erro silencioso ao limpar cache
        }
      }

      const { error } = await supabase.auth.signOut()

      if (error) throw error

      // Registrar log de logout (antes de limpar o estado)
      if (userIdBeforeLogout) {
        try {
          await logUserAction({
            action: 'logout',
            entityType: 'other',
            entityId: null,
            description: `Logout realizado no sistema`,
            userId: userIdBeforeLogout,
          })
        } catch (logError) {
          // Não bloquear o logout em caso de erro no log
          console.warn('Erro ao registrar log de logout:', logError)
        }
      }

      setAuthState({
        user: null,
        profile: null,
        session: null,
        loading: false,
        error: null
      })

      return { success: true }
    } catch (error) {
      const errorMessage = (error as Error).message
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  // Reset de senha
  const resetPassword = async (email: string): Promise<AuthResult> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }))

      const { data, error } = await supabase.auth.resetPasswordForEmail(normalizeEmail(email), {
        redirectTo: `${window.location.origin}/reset-password`
      })

      if (error) throw error

      setAuthState(prev => ({ ...prev, loading: false, error: null }))

      return { success: true }
    } catch (error) {
      const errorMessage = (error as Error).message
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  // Atualizar senha
  const updatePassword = async (password: string): Promise<AuthResult> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }))

      const { data, error } = await supabase.auth.updateUser({
        password: password
      })

      if (error) throw error

      setAuthState(prev => ({ ...prev, loading: false, error: null }))

      return { success: true }
    } catch (error) {
      const errorMessage = (error as Error).message
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  // Atualizar perfil
  const updateProfile = async (profileData: Partial<UserProfile>): Promise<AuthResult> => {
    try {
      if (!authState.user) {
        return { success: false, error: 'Usuário não autenticado' }
      }

      setAuthState(prev => ({ ...prev, loading: true, error: null }))

      const { data, error } = await supabase
        .from('profiles')
        .update(profileData)
        .eq('id', authState.user.id)
        .select()
        .single()

      if (error) throw error

      setAuthState(prev => ({
        ...prev,
        profile: data,
        loading: false,
        error: null
      }))

      return { success: true }
    } catch (error) {
      const errorMessage = (error as Error).message
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  // Verificar se o usuário é admin
  const isAdmin = useCallback(() => {
    return authState.profile?.user_group?.is_admin || false
  }, [authState.profile])

  // Verificar se o usuário está ativo
  const isActive = useCallback(() => {
    return authState.profile?.is_active || false
  }, [authState.profile])

  return {
    user: authState.user,
    profile: authState.profile,
    session: authState.session,
    loading: authState.loading,
    error: authState.error,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    isAdmin,
    isActive
  }
}
